import { BrandOAuthProvider } from "./brand"

export enum ClientChannelType {
  WEB = "web",
  WIDGET = "widget",
  BROWSER_EXTENSION = "browser_extension",
  SLACK = "slack",
  LINE = "line",
  MESSENGER = "messenger",
  INSTAGRAM = "instagram",
}

export interface WebAdButton {
  text: string
  url: string
}

export interface DescriptionItem {
  type: "text" | "link" | "email"
  text: string
  url?: string
}

export interface WebClientConfig {
  type: "web"
  title: string
  descriptions: DescriptionItem[]
  allowAnonymousAccess?: boolean
  validRedeemCode?: string
  loginImageUrl?: string
  headerImageUrl?: string
  adButtons: WebAdButton[]
}

export type WidgetOriginPolicyMode = "disabled" | "configured_origins_only"

export type WidgetLoginVerificationMode = "assertion" | "direct_unverified"

export type WidgetPosition = "bottom-right" | "bottom-left"

export type WidgetLocale = "en" | "zh-TW" | "ja"

export type WidgetBaseColor =
  | "default"
  | "blue"
  | "green"
  | "orange"
  | "red"
  | "rose"
  | "violet"
  | "yellow"

export interface WidgetUiConfig {
  title: string
  position: WidgetPosition
  locale: WidgetLocale
  launcher: {
    label: string
    iconUrl?: string
  }
  panel: {
    greeting?: string
    suggestions?: string[]
  }
  theme: {
    baseColor: WidgetBaseColor
  }
}

export interface WidgetClientConfig {
  type: "widget"
  agentId?: string
  allowAnonymousAccess: boolean
  loginEnabled: boolean
  loginVerificationMode: WidgetLoginVerificationMode
  originPolicyMode: WidgetOriginPolicyMode
  allowedOrigins: string[]
  ui: WidgetUiConfig
}

export interface ExtensionClientConfig {
  type: "browser_extension"
  extensionId: string
}

export interface SlackClientConfig {
  type: "slack"
  botToken: string
  signingSecret: string
  teamId: string
  teamName: string
  botUserId?: string
  agentId: string
}

export interface LineClientConfig {
  type: "line"
  channelSecret: string
  channelAccessToken: string
  liffId: string
  lineLoginChannelId: string
  agentId: string
  requireEmailVerification: boolean
  skipKeywords: string[]
  conversationSessionTimeoutHours?: number
}

export interface MessengerClientConfig {
  type: "messenger"
  pageId: string
  webhookVerifyToken: string
  metaAppSecret: string
  accessToken: string
  agentId: string
  conversationSessionTimeoutHours?: number
}

export interface InstagramClientConfig {
  type: "instagram"
  accountId: string
  webhookVerifyToken: string
  metaAppSecret: string
  accessToken: string
  agentId: string
  conversationSessionTimeoutHours?: number
}

export type ClientChannelConfig =
  | WebClientConfig
  | WidgetClientConfig
  | ExtensionClientConfig
  | SlackClientConfig
  | LineClientConfig
  | MessengerClientConfig
  | InstagramClientConfig

export interface WidgetPrivateAuthState {
  issuer?: string
  hasSharedSecret: boolean
  secretLastRegeneratedAt?: Date
}

export interface ClientChannel {
  id: string
  name: string
  slug: string
  type: ClientChannelType
  workspaceId: string
  config: ClientChannelConfig
  widgetPrivateAuth?: WidgetPrivateAuthState
  isHealthy: boolean
  isPublished: boolean
  lastPublishedSlug: string
  lastPublishedVersion: string
  publishUrl: string
  publishedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface ClientChannelListItem {
  id: string
  name: string
  slug: string
  type: ClientChannelType
  agentId: string
  isHealthy: boolean
  isPublished: boolean
  lastPublishedSlug: string
  lastPublishedVersion: string
  publishedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface ClientChannelOAuthBinding {
  id: string
  clientChannelId: string
  oauthProvider: BrandOAuthProvider
  createdAt: Date
  updatedAt: Date
}

// Form data types
export interface ClientChannelEditData {
  name: string
  slug: string
  apiKeyPrefix?: string
}

export interface ClientChannelCreateData {
  name: string
  slug: string
  type: ClientChannelType
  apiKeyPrefix?: string
}

export interface WebClientConfigEditData {
  name: string
  descriptions: DescriptionItem[]
  title: string
  validRedeemCode?: string
  adButtons: WebAdButton[]
}

export interface ExtensionClientConfigEditData {
  name: string
  description: string
  extensionId: string
}

// Type guards
export function isWebClientConfig(
  config: ClientChannelConfig
): config is WebClientConfig {
  return config.type === "web"
}

export function isWidgetClientConfig(
  config: ClientChannelConfig
): config is WidgetClientConfig {
  return config.type === "widget"
}

export function isExtensionClientConfig(
  config: ClientChannelConfig
): config is ExtensionClientConfig {
  return config.type === "browser_extension"
}

export function isSlackClientConfig(
  config: ClientChannelConfig
): config is SlackClientConfig {
  return config.type === "slack"
}

export function isLineClientConfig(
  config: ClientChannelConfig
): config is LineClientConfig {
  return config.type === "line"
}

export const normalizeLineSkipKeywords = (
  keywords: readonly string[] = []
): string[] => {
  const seen = new Set<string>()
  const cleaned: string[] = []

  for (const keyword of keywords) {
    if (keyword === "" || seen.has(keyword)) continue
    seen.add(keyword)
    cleaned.push(keyword)
  }

  return cleaned
}

export function isMessengerClientConfig(
  config: ClientChannelConfig
): config is MessengerClientConfig {
  return config.type === "messenger"
}

export function isInstagramClientConfig(
  config: ClientChannelConfig
): config is InstagramClientConfig {
  return config.type === "instagram"
}
